import { useEffect, useState } from 'react';

/**
 * Hook to detect if the mobile keyboard is currently visible.
 * Uses the Visual Viewport API when available, with fallback to window.innerHeight.
 *
 * @param threshold - The percentage of viewport height reduction to consider as keyboard shown (default: 0.15 = 15%)
 * @returns boolean indicating if the keyboard is visible
 */
function useMobileKeyboard(threshold: number = 0.15): boolean {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if Visual Viewport API is available (preferred method)
    const hasVisualViewport =
      'visualViewport' in window && window.visualViewport !== null;

    let initialHeight: number;
    let currentHeight: number;

    const updateKeyboardState = () => {
      if (hasVisualViewport && window.visualViewport) {
        currentHeight = window.visualViewport.height;
        // Visual Viewport height decreases when keyboard appears
        const heightDiff = initialHeight - currentHeight;
        const heightReduction = heightDiff / initialHeight;

        setIsKeyboardVisible(heightReduction > threshold);
      } else {
        // Fallback: use window.innerHeight
        currentHeight = window.innerHeight;
        const heightDiff = initialHeight - currentHeight;
        const heightReduction = heightDiff / initialHeight;

        setIsKeyboardVisible(heightReduction > threshold);
      }
    };

    // Initialize height
    if (hasVisualViewport && window.visualViewport) {
      initialHeight = window.visualViewport.height;
    } else {
      initialHeight = window.innerHeight;
    }

    // Set up event listeners
    if (hasVisualViewport && window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateKeyboardState);
      window.visualViewport.addEventListener('scroll', updateKeyboardState);
    } else {
      window.addEventListener('resize', updateKeyboardState);
    }

    // Also check on focus/blur of input elements as a secondary indicator
    const handleFocus = () => {
      // Small delay to allow viewport to adjust
      setTimeout(updateKeyboardState, 100);
    };

    const handleBlur = () => {
      // Small delay to allow viewport to adjust
      setTimeout(updateKeyboardState, 100);
    };

    // Listen to focus/blur on all input elements
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    // Cleanup
    return () => {
      if (hasVisualViewport && window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateKeyboardState
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateKeyboardState
        );
      } else {
        window.removeEventListener('resize', updateKeyboardState);
      }
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, [threshold]);

  return isKeyboardVisible;
}

export default useMobileKeyboard;
